import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import { useGuardedMutation } from "model/useGuardedMutation";
import { ActionFileEntry } from "./ActionFileEntry";

export type { ActionFileEntry };

async function getActionEvidence(
  actionSubmissionGuid: string
): Promise<ActionFileEntry[]> {
  return fetchApi(
    `actionFileEntry?actionSubmissionGuid=${encodeURIComponent(
      actionSubmissionGuid
    )}`,
    undefined,
    "v2"
  ) as Promise<ActionFileEntry[]>;
}

export interface CreateActionFileEntryParams {
  fileEntryGuid: string;
  actionSubmissionGuid: string;
  fileName: string;
  fileUploadType: string;
  fileEntryFileUploadGuid: string;
  uploaderGuid: string;
}

async function createActionFileEntry(
  params: CreateActionFileEntryParams
): Promise<{ guid: string }> {
  return fetchApi(
    "actionFileEntry",
    {
      method: "POST",
      body: JSON.stringify({
        fileEntryGuid: params.fileEntryGuid,
        actionSubmissionGuid: params.actionSubmissionGuid,
      }),
    },
    "v2"
  ) as Promise<{ guid: string }>;
}

async function deleteActionFileEntry(guid: string): Promise<void> {
  await fetchApi(`actionFileEntry/${guid}`, { method: "DELETE" }, "v2");
}

export function useActionEvidence(actionSubmissionGuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.actionEvidence.bySubmission(actionSubmissionGuid ?? ""),
    queryFn: () => getActionEvidence(actionSubmissionGuid ?? ""),
    enabled: !!actionSubmissionGuid,
  });
}

export function useCreateActionFileEntry() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: createActionFileEntry,
    onSuccess: (data, variables) => {
      const key = queryKeys.actionEvidence.bySubmission(
        variables.actionSubmissionGuid
      );
      const newEntry: ActionFileEntry = {
        guid: data.guid,
        actionSubmissionGuid: variables.actionSubmissionGuid,
        creationDate: new Date().toISOString(),
        fileEntryGuid: variables.fileEntryGuid,
        fileDescription: "",
        fileUploadType: variables.fileUploadType,
        fileName: variables.fileName,
        fileEntryFileUploadGuid: variables.fileEntryFileUploadGuid,
        uploaderGuid: variables.uploaderGuid,
      };
      queryClient.setQueryData<ActionFileEntry[]>(key, (old = []) => [
        ...old,
        newEntry,
      ]);
    },
  });
}

export function useDeleteActionFileEntry() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (params: {
      guid: string;
      actionSubmissionGuid: string;
    }) => {
      await deleteActionFileEntry(params.guid);
      return params;
    },
    onSuccess: (params) => {
      const key = queryKeys.actionEvidence.bySubmission(
        params.actionSubmissionGuid
      );
      queryClient.setQueryData<ActionFileEntry[]>(key, (old = []) =>
        old.filter((e) => e.guid !== params.guid)
      );
    },
  });
}
